import React, { useContext } from 'react';
import styled from 'styled-components';
import { AsyncSearch as BaseAsyncSearch } from '@ubeya/shared-web/components';
import useAvailableEmployeeByTimeAndPosition from '@ubeya/shared/hooks/account/useAvailableEmployeeByTimeAndPosition';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { baseInputStyle } from '@ubeya/shared-web/components/Input/InputStyle';
import { EMPLOYEE_FIELD_BIRTHDAY } from '@ubeya/shared-web/utils/employee/constants';
import BaseUserCard, { USER_CARD_HEIGHT } from '../../ManageBooking/UserCard';
import EmployeeBookingWarningsRenderer from '../../ManageBooking/EmployeeBookingWarningsRenderer';
import { useAvailabilityEmployeeWarnings } from '../../ManageBooking/useAvailabilityEmployeeWarnings';
import { REJECTED_EMPLOYEES, SLOT_WARNING_TYPES, WARNING_SEVERITY_OPTIONS } from 'scheduling/constants';
import {
  generateUniqueWarnings,
  generateUniqueWarningsSlugs,
  getEmployeeAgeInfo,
  warningMetadataFactory
} from 'scheduling/utils';

const UserCard = styled(BaseUserCard)`
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 4px;
`;

const AsyncSearch = styled(BaseAsyncSearch)`
  height: unset;
  border-radius: unset;
  > input {
    ${baseInputStyle};
  }
`;

const InlineData = styled.div`
  font-size: 10px;
`;

const RowWrapper = styled.div`
  position: relative;
  &:has(.booking-warnings-error-icon) {
    padding-right: 20px;
  }
  .booking-warnings-error-icon {
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
  }
`;

const EmptyOption = styled.div`
  display: flex;
  max-height: ${USER_CARD_HEIGHT}px;
  min-height: ${USER_CARD_HEIGHT}px;
  justify-content: center;
  align-items: center;
`;

const AssignToFieldRow = ({
  rowData,
  positionId,
  timeFormat,
  setEmployee,
  resetTimesheetData,
  projectId,
  shiftId,
  branchId,
  t
}) => {
  const { isBlockDemographicFiltersInSchedulingEnabled } = useContext(ConfigContext);
  const flattenWarnings = generateUniqueWarnings(rowData.warnings || []) || [];
  const rejected = REJECTED_EMPLOYEES.includes(rowData?.listType);

  const { explanationMarkWarnings, shouldRenderWarningBorder } = useAvailabilityEmployeeWarnings({
    user: rowData,
    positionId,
    warnings: [],
    rejected
  });

  const warnings = flattenWarnings
    .map(({ slug, metadata }) =>
      [t(slug), warningMetadataFactory({ listType: rowData.listType, metadata, timeFormat })]
        .filter((cur) => !!cur)
        .join(' ')
    )
    .join(', ');

  const hasWarnings =
    flattenWarnings.filter(({ severity }) => severity !== WARNING_SEVERITY_OPTIONS.WARNING).length > 0 ||
    (rowData?.warnings || []).some((w) => w.type === SLOT_WARNING_TYPES.WTD.type);

  const fieldsArray = rowData?.fields ?? [];
  const fieldsObj = fieldsArray.reduce((acc, f) => {
    acc[f.id] = f.value;
    return acc;
  }, {});
  const employeeWithFields = { ...rowData, fields: fieldsObj };

  const birthdateStr = fieldsObj[EMPLOYEE_FIELD_BIRTHDAY];
  const ageResult = getEmployeeAgeInfo(birthdateStr, { isAgeHidden: isBlockDemographicFiltersInSchedulingEnabled });
  const { age } = ageResult;

  return (
    <RowWrapper>
      <UserCard
        shouldRenderWarningBorder={shouldRenderWarningBorder}
        user={employeeWithFields}
        hasWarnings={hasWarnings}
        inlineData={[<InlineData key="inline-data-warnings">{warnings}</InlineData>]}
        onClick={() => {
          setEmployee({
            employee: employeeWithFields,
            warnings: {
              explanationMarkWarnings,
              slotWarnings: generateUniqueWarningsSlugs(rowData.warnings || []),
              inlineWarnings: warnings,
              shouldRenderWarningBorder,
              hasWarnings
            }
          });
          resetTimesheetData?.();
        }}
      />
      <EmployeeBookingWarningsRenderer
        externalFormattedWarnings={explanationMarkWarnings}
        slotWarnings={generateUniqueWarningsSlugs(rowData.warnings || [])}
        projectId={projectId}
        shiftId={shiftId}
        branchId={branchId}
        user={rowData}
        age={age}
        isAvailabilityContext
      />
    </RowWrapper>
  );
};

const AssignToField = ({
  resetTimesheetData,
  setAddEmployeeModal,
  branchId,
  startTime,
  filterBookedEmployees = undefined,
  timeFormat,
  setEmployee,
  endTime,
  positionId,
  shiftId,
  projectId
}) => {
  const { t } = useContext(ConfigContext);

  return (
    <AsyncSearch
      usePortal
      supportEmptyState
      optionsWidth={430}
      menuGap={8}
      hook={useAvailableEmployeeByTimeAndPosition}
      params={{
        enabled: true,
        employeesBlacklist: filterBookedEmployees,
        branchId,
        startTime,
        endTime,
        positionId: positionId || undefined,
        shiftId,
        projectId
      }}
      optionHeight={USER_CARD_HEIGHT}
      renderRow={({ rowData }) => {
        if (rowData?.emptyState) {
          return (
            <EmptyOption
              onClick={() =>
                setAddEmployeeModal({
                  ...(rowData?.value && { firstName: rowData?.value }),
                  ...(positionId && { positions: [positionId] }),
                  ...(branchId && { branches: [branchId] })
                })
              }>
              + {rowData?.value ? t('addAsNewEmployee', { firstName: rowData?.value }) : t('addEmployee')}
            </EmptyOption>
          );
        }

        return (
          <AssignToFieldRow
            rowData={rowData}
            positionId={positionId}
            timeFormat={timeFormat}
            setEmployee={setEmployee}
            resetTimesheetData={resetTimesheetData}
            projectId={projectId}
            shiftId={shiftId}
            branchId={branchId}
            t={t}
          />
        );
      }}
    />
  );
};

export default React.memo(AssignToField);
